import { lazy, Suspense, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { TwcBackground } from '@atlassian/product-wireframes';
import { SOURCE_COMPONENT, SOURCE_CONTEXT } from '@atlassian/twc-xflow-modal/constants';

import { TwcOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';
import { Dropdown } from '@/components/signup/site-activation/components/common/dropdown';
import { useSiteActivationContext } from '@/components/signup/site-activation/hooks/site-activation-context';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { useCrossflowEligibleSites } from '@/components/user-context/helpers/cohorts';
import { Button } from '@/libraries/design/components/button/src/button';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLocation } from '@/utils/location-provider';

import { Headings } from '../../components/headings';
import { SwitchYourSiteMessage } from '../components/switch-your-site-message';
import { getLoomTwcWelcomeUrl } from '../loom-twc-welcome-url';

const TwcXflowModal = lazy(() =>
	import('@atlassian/twc-xflow-modal').then(({ TwcXflowModal }) => ({ default: TwcXflowModal })),
);

export const CrossflowEligibleUser = () => {
	const [cloudId, setCloudId] = useState<string | null>(null);
	const location = useLocation();
	const shouldOpenCrossflow =
		checkFeatureFlagGate(featureFlags.twcg1930TwcWacHomepage) &&
		new URLSearchParams(location.search).has('openTwcXflowModal');
	const { locale } = useIntl();
	const headingText = (
		<FormattedMessage
			id="signup.teamworkCollection.uplift.heading"
			defaultMessage="Switch your site to Teamwork Collection"
		/>
	);

	if (cloudId) {
		return (
			<TwcBackground>
				<Suspense fallback={null}>
					<TwcXflowModal
						cloudId={cloudId}
						locale={locale}
						onClose={() => setCloudId(null)}
						onComplete={() =>
							window.location.assign(getLoomTwcWelcomeUrl(cloudId, { skipUpgrade: true }))
						}
						sourceComponent={SOURCE_COMPONENT.WAC}
						sourceContext={SOURCE_CONTEXT.WAC_HOMEPAGE_TWC_OFFER}
						testId="wac-twc-xflow-modal"
					/>
				</Suspense>
			</TwcBackground>
		);
	}

	return (
		<TwcOverlayTemplate size="sm">
			<Headings main={headingText} />
			<SiteActivation
				CrossflowEligibleUser={
					shouldOpenCrossflow ? (
						<CrossflowXflowPicker onSelectSite={setCloudId} />
					) : (
						<SwitchYourSiteMessage />
					)
				}
			/>
		</TwcOverlayTemplate>
	);
};

const CrossflowXflowPicker = ({ onSelectSite }: { onSelectSite: (cloudId: string) => void }) => {
	const { sitePickerValue } = useSiteActivationContext();
	const crossflowEligibleSites = useCrossflowEligibleSites();

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (sitePickerValue) {
			onSelectSite(sitePickerValue.cloudId);
		}
	};

	if (!crossflowEligibleSites.length) {
		return <SwitchYourSiteMessage />;
	}

	return (
		<form css={formCss} onSubmit={onSubmit}>
			<Dropdown sites={crossflowEligibleSites} />
			<Button css={buttonCss} type="submit">
				<FormattedMessage
					id="siteActivation.nameSiteNewUser.continueButton"
					defaultMessage="Continue"
				/>
			</Button>
		</form>
	);
};

const formCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300', '24px'),
	width: '100%',
});

const buttonCss = css({
	maxWidth: 'none',
});
